// hl-custom: custom file, not in stock Horizon -- price range slider: dual-handle slider mirroring the price facet min/max inputs (rendered by snippets/price-filter.liquid)
import { Component } from '@theme/component';
import { convertMoneyToMinorUnits } from '@theme/money-formatting';

/**
 * @typedef {Object} PriceRangeSliderRefs
 * @property {HTMLInputElement} minRange - The lower-bound range input
 * @property {HTMLInputElement} maxRange - The upper-bound range input
 */

/**
 * A dual-handle range slider that mirrors the price facet's min/max text inputs.
 *
 * The slider never talks to the filtering pipeline directly: it writes whole major-unit
 * values into the text inputs and lets the native `change` event of the range inputs
 * bubble to `price-facet-component[on:change]`, which runs the existing update flow.
 * Values are in major currency units (all store markets use 2-decimal currencies).
 *
 * @extends {Component<PriceRangeSliderRefs>}
 */
class PriceRangeSlider extends Component {
  requiredRefs = ['minRange', 'maxRange'];

  get #facet() {
    return this.closest('price-facet-component');
  }

  /**
   * @param {'minInput' | 'maxInput'} name
   * @returns {HTMLInputElement | null}
   */
  #textInput(name) {
    return this.#facet?.querySelector(`input[ref="${name}"]`) ?? null;
  }

  get #rangeMax() {
    return Number(this.dataset.rangeMax) || 0;
  }

  connectedCallback() {
    super.connectedCallback();
    this.refs.minRange.addEventListener('input', this.#onSliderInput);
    this.refs.maxRange.addEventListener('input', this.#onSliderInput);
    this.#facet?.addEventListener('change', this.#onFacetChange);
    this.#syncFromInputs();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.minRange?.removeEventListener('input', this.#onSliderInput);
    this.refs.maxRange?.removeEventListener('input', this.#onSliderInput);
    this.#facet?.removeEventListener('change', this.#onFacetChange);
  }

  /**
   * Re-align the handles after the Section Rendering API + morph re-render the facet.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#syncFromInputs();
  }

  /**
   * A handle is being dragged: keep min ≤ max and mirror the values into the text
   * inputs live. At the outer bounds the text inputs are emptied so the filter
   * parameters are dropped from the URL entirely.
   *
   * @param {Event} event
   */
  #onSliderInput = (event) => {
    const { minRange, maxRange } = this.refs;
    let minValue = Number(minRange.value);
    let maxValue = Number(maxRange.value);

    if (minValue > maxValue) {
      if (event.target === minRange) {
        minValue = maxValue;
        minRange.value = String(minValue);
      } else {
        maxValue = minValue;
        maxRange.value = String(maxValue);
      }
    }

    const minInput = this.#textInput('minInput');
    const maxInput = this.#textInput('maxInput');
    if (minInput) minInput.value = minValue <= 0 ? '' : String(minValue);
    if (maxInput) maxInput.value = maxValue >= this.#rangeMax ? '' : String(maxValue);

    this.#render(minValue, maxValue);
  };

  /**
   * A `change` bubbled through the facet. Ignore the ones coming from our own range
   * inputs; anything else means the text inputs were edited (or cleared) — re-align.
   *
   * @param {Event} event
   */
  #onFacetChange = (event) => {
    if (event.target === this.refs.minRange || event.target === this.refs.maxRange) return;
    this.#syncFromInputs();
  };

  #syncFromInputs() {
    const rangeMax = this.#rangeMax;
    const minValue = clamp(this.#parseInput('minInput') ?? 0, 0, rangeMax);
    const maxValue = clamp(this.#parseInput('maxInput') ?? rangeMax, minValue, rangeMax);

    this.refs.minRange.value = String(Math.floor(minValue));
    this.refs.maxRange.value = String(Math.ceil(maxValue));
    this.#render(minValue, maxValue);
  }

  /**
   * Parses a text input's display value ("1 234,56", "1,234.56", …) into major units.
   *
   * @param {'minInput' | 'maxInput'} name
   * @returns {number | null}
   */
  #parseInput(name) {
    const value = this.#textInput(name)?.value.trim();
    if (!value) return null;

    const currency = this.#facet?.getAttribute('data-currency') ?? 'USD';
    const minorUnits = convertMoneyToMinorUnits(value, currency);
    return minorUnits == null ? null : minorUnits / 100;
  }

  /**
   * Positions the active-track fill and keeps the handle that still has room to move
   * on top when both handles collapse onto the same end of the track.
   *
   * @param {number} minValue
   * @param {number} maxValue
   */
  #render(minValue, maxValue) {
    const rangeMax = this.#rangeMax || 1;
    this.style.setProperty('--slider-min-pct', `${(minValue / rangeMax) * 100}%`);
    this.style.setProperty('--slider-max-pct', `${(maxValue / rangeMax) * 100}%`);

    const minOnTop = minValue > rangeMax / 2;
    this.refs.minRange.style.zIndex = minOnTop ? '4' : '3';
    this.refs.maxRange.style.zIndex = minOnTop ? '3' : '4';
  }
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

if (!customElements.get('price-range-slider')) {
  customElements.define('price-range-slider', PriceRangeSlider);
}
